import React, { createContext, useContext, useMemo, useRef } from 'react';

import { reportCardPlayerName, ReportCardActionsApi, useReportCardActions } from './report-card-actions.use-actions';
import { ReportCardWriteConfirmDialog } from './report-card-write-confirm-dialog.component';

// What each row reads. The confirm state stays with the provider's dialog, so opening or
// cancelling a confirm doesn't re-render every row of a long table.
type ReportCardRowActions = Omit<ReportCardActionsApi, 'confirming' | 'confirm' | 'cancel'>;

const ReportCardActionsContext = createContext<ReportCardRowActions | null>(null);

interface IReportCardActionsProviderProps {
  children: React.ReactNode;
}

// Builds the report card writes once for a table or page, with the one confirm dialog they share.
export const ReportCardActionsProvider: React.FC<IReportCardActionsProviderProps> = ({ children }) => {
  const actions = useReportCardActions();
  const { confirming, messages, surfaceFor, isInFlight, request, copyLink, view } = actions;
  // Keeps the last player's name on screen while the dialog fades out after a choice.
  const shown = useRef(confirming);
  if (confirming) shown.current = confirming;
  const rowActions = useMemo(() => ({ messages, surfaceFor, isInFlight, request, copyLink, view }),
    [messages, surfaceFor, isInFlight, request, copyLink, view]);

  return (
    <ReportCardActionsContext.Provider value={rowActions}>
      {children}
      <ReportCardWriteConfirmDialog
        action={confirming?.action ?? null}
        name={shown.current ? reportCardPlayerName(shown.current.playerAssessment) : ''}
        messages={actions.messages}
        onConfirm={actions.confirm}
        onCancel={actions.cancel}
      />
    </ReportCardActionsContext.Provider>
  );
};

export const useReportCardActionsContext = (): ReportCardRowActions => {
  const actions = useContext(ReportCardActionsContext);
  if (!actions) throw new Error('ReportCardActions must be rendered inside a ReportCardActionsProvider.');
  return actions;
};
